import React from 'react';
import * as _ from 'lodash';

import FieldTreeResultContext from './FieldTreeResultContext';
import FieldTreeResultApi from './FieldTreeResultApi';
import FieldTreeResultService from './FieldTreeResultService';

import SessionStorageService from '../utilities/SessionStorageService';

/**
 * 実績集計のstate管理用コンポーネント
 */
export default class FieldTreeResultStore extends React.Component {
  state = {
    fieldId: SessionStorageService.getFieldId() || null,
    isLoading: false,
    loaded: false,
    fieldTreeResultData: null,
    fieldTreeResultList: [],
    resultsTotal: 0,
    searchParams: {},
    filterParams: {
      displayIndividual: true,
      displayTotal: true,
    },
    isOpenAllItems: false,
  };

  /**
   * 検索パラメータ変更ハンドラ
   * @param {Object} newSearchParams 新しい検索パラメータ
   * @param {string} newSearchParams.dateFrom
   * @param {string} newSearchParams.dateTo
   * @param {number} newSearchParams.isUnfulfillRate
   */
  handleSearchParamsChange = newSearchParams => {
    this.setState({ searchParams: newSearchParams, loaded: false }, () => {
      this.clearData();
      this.loadFieldTreeResultList();
    });
  };

  /**
   * フィルターパラメータ変更ハンドラ
   * @param {Object} newFilterParams 新しいフィルターパラメータ
   * @param {boolean} displayIndividual 個社の値の表示フラグ
   * @param {boolean} displayTotal 合計の値の表示フラグ
   */
  handleFilterParamsChange = newFilterParams => {
    this.setState({ filterParams: newFilterParams });
  };

  /**
   * テーブル行の開閉トグル
   * @param {number} itemId fieldTreeResultのID
   */
  toggleTableRow = itemId => {
    this.setState(oldState => {
      let state = { ...oldState };
      const item = state.fieldTreeResultList.find(x => x.id == itemId);
      item.isOpened = !item.isOpened;
      return state;
    });
  };

  /**
   * テーブルのすべての行の開閉トグル
   */
  toggleAllItems = () => {
    this.setState(oldState => {
      const items = oldState.fieldTreeResultList;
      items.forEach(item => {
        item.isOpened = !oldState.isOpenAllItems;
      });
      return {
        isOpenAllItems: !oldState.isOpenAllItems,
        fieldTreeResultList: items,
      };
    });
  };

  /**
   * 無限スクロールから呼び出される
   * @param {function} notify
   */
  infiniteScrollLoad = async notify => {
    await this.loadFieldTreeResultList();
    if (typeof notify === 'function') {
      notify();
    }
  };

  /**
   * 実績集計を取得する
   * */
  loadFieldTreeResultList = async () => {
    if (this.state.loaded || this.state.isLoading) {
      return;
    }
    await this.setLoading(true);
    try {
      const oldSearchParams = { ...this.state.searchParams };
      const response = await FieldTreeResultApi.fetchFieldTreeResultList(
        this.state.fieldId,
        FieldTreeResultService.createSearchApiParam({
          ...this.state.searchParams,
          offset: this.state.fieldTreeResultList.filter(
            item => item.nest_no === 1
          ).length,
        })
      );
      if (!response.result) {
        throw response.message;
      }
      const data = response.data;
      if (!_.isEqual(oldSearchParams, this.state.searchParams)) {
        await this.setLoading(false);
        await this.loadFieldTreeResultList();
        return;
      }
      const state = { ...this.state };
      state.fieldTreeResultData = _.omit(data.entities, 'field_tree_results');
      state.resultsTotal = data.total;
      if (_.isEmpty(data.entities.field_tree_results)) {
        state.loaded = true;
      } else {
        state.fieldTreeResultList.push(...data.entities.field_tree_results);
        this.processResults(
          state.fieldTreeResultData,
          state.fieldTreeResultList
        );
        this.determineHierarchy(state.fieldTreeResultList);
      }
      this.setState(state);
      this.setLoading(false);
    } catch (error) {
      alert(error);
    } finally {
      await this.setLoading(false);
    }
  };

  /**
   * 表示されているデータの消去
   */
  clearData = () => {
    this.setState({
      fieldTreeResultData: null,
      fieldTreeResultList: [],
    });
  };

  /**
   * @param {boolean} isLoading
   */
  setLoading = isLoading => {
    return new Promise(resolve => {
      this.setState({ isLoading }, resolve);
    });
  };

  /**
   * APIからのデータの処理
   */
  processResults = (fieldTreeResultData, fieldTreeResultList) => {
    fieldTreeResultList.forEach(item => {
      item.isMyCompany = item.my_company_flag === '1';
      item.isPrimeChild =
        fieldTreeResultData.prime_contractors_subcontract_code === 1;
      item.canBeCollapsed =
        item.isMyCompany || (item.isPrimeChild && item.nest_no === 1);
      if (_.isUndefined(item.isOpened)) {
        item.isOpened = this.state.isOpenAllItems;
      }
      item.fulfillmentRateComplete = parseInt(item.fulfillment_rate) >= 100;
      item.fulfillmentRateTotalComplete =
        parseInt(item.fulfillment_rate_total) >= 100;
    });
  };

  /**
   * 階層の決定
   */
  determineHierarchy = fieldTreeResults => {
    let myCompanyBranch = false;
    let myCompanyNestNo = 0;
    fieldTreeResults.forEach((item, index) => {
      if (
        index + 1 >= fieldTreeResults.length ||
        fieldTreeResults[index + 1].nest_no <= item.nest_no
      ) {
        item.canBeCollapsed = false;
      }

      if (myCompanyBranch) {
        if (item.nest_no <= myCompanyNestNo) {
          myCompanyBranch = false;
        }
      }
      item.isChild = myCompanyBranch;
      if (item.my_company_flag === '1') {
        myCompanyBranch = true;
        myCompanyNestNo = item.nest_no;
      }
    });
  };

  /**
   * @returns {Array} 表示されるfieldTreeResultの項目
   */
  filterVisibleFieldTreeResults = () => {
    let visibleItems = [];
    let branchClosed = false;
    let closedFromNestNo = 0;
    this.state.fieldTreeResultList.forEach(item => {
      if (branchClosed) {
        if (item.nest_no > closedFromNestNo) {
          return;
        } else {
          branchClosed = false;
        }
      }
      visibleItems.push(item);
      if (item.canBeCollapsed && !item.isOpened) {
        branchClosed = true;
        closedFromNestNo = item.nest_no;
      }
    });
    return visibleItems;
  };

  render() {
    const visibleFieldTreeResultList = this.filterVisibleFieldTreeResults();
    return (
      <FieldTreeResultContext.Provider
        value={{
          state: this.state,
          visibleFieldTreeResultList,
          closeNotExistFieldIdDialog: this.closeNotExistFieldIdDialog,
          infiniteScrollLoad: this.infiniteScrollLoad,
          toggleAllItems: this.toggleAllItems,
          toggleTableRow: this.toggleTableRow,
          handleSearchParamsChange: this.handleSearchParamsChange,
          handleFilterParamsChange: this.handleFilterParamsChange,
        }}
      >
        {this.props.children}
      </FieldTreeResultContext.Provider>
    );
  }
}



// WEBPACK FOOTER //
// ./src/services/fieldTreeResult/FieldTreeResultStore.js